import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Label,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import PhaseCurrentSelectItem, { PhaseCurrentSelectData } from './PhaseCurrentSelectItem';
import { GetPhaseCurrentGraph, GraphLineData, GraphLineDatas } from './PhaseCurrent';
import { PhaseCurrentCheckBoxData } from './PhaseCurrentCheckBoxList';
import { SelectMsecondType } from './PhaseCurrentPage';
import * as Constant from '../../../Constant';
import { ToValueRoundInternal, ToPowIndex, ToValueMaxRound, ToValueMinRound } from '../../../function/CalculationModel';
import { useTranslation } from 'react-i18next';
import { GetMemoryScaleByNumber } from '../../../function/ChartCommon';

const useStyles = makeStyles(
  createStyles({
    customTooltip: {
      background: '#FFFFEE',
      fontSize: '14px',
      padding: '10px 10px 0px 20px',
    },
  })
);

type GraphData = {
  lineNo: number;
  lineName: string;
  points: GraphPoint[];
  valueUpper: number;
  valueUpperLimit: number;
  selectFlg: boolean;
  infoColor: string;
};
type GraphPoint = {
  xValue: number;
  yValue: number;
};

type GraphSetting = {
  upper: number;
  upperLimit: number;
  yMin: number;
  yMax: number;
  xMin: number;
  xMax: number;
  backColor: string;
  memoryFontColor: string;
  yUnit: string;
  yUnitColor: string;
  xUnitColor: string;
  gridColor: string;
};

type Props = {
  selectMSecondData: SelectMsecondType;
  selectData: PhaseCurrentSelectData;
  checkBoxData: PhaseCurrentCheckBoxData;
  chartUpDate: string;
  chartContainerStyle: string;
  updateLoadginState: (value: boolean) => void;
};

const PhaseCurrentChart: React.FC<Props> = (props) => {
  console.debug(['PhaseCurrentChart props', props]);
  const { t } = useTranslation();

  const classes = useStyles();
  const [graphData, setGraphData] = useState<GraphData[]>([]);
  const [graphLineData, setGraphLineData] = useState<GraphLineDatas>();
  const [graphSetting, setGraphSetting] = useState<GraphSetting>({
    upper: 0,
    upperLimit: 0,
    yMax: 0,
    yMin: 0,
    xMax: 0,
    xMin: 0,
    backColor: Constant.GraphThemeLight.BACK_COLOR,
    memoryFontColor: Constant.GraphThemeLight.MEMORY_FONT_COLOR,
    yUnit: '',
    yUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    xUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    gridColor: Constant.GraphThemeLight.GRID_COLOR,
  });

  useEffect(() => {
    console.debug(['useEffect -- props.chartUpDate', props.chartUpDate, props.selectData.devIndex]);
    if (props.selectData.devIndex == '0') {
      //初期化時は呼ばない
      return;
    }
    getGraphData(props.selectMSecondData.maxSec);
  }, [props.chartUpDate]);

  useEffect(() => {
    console.debug(['useEffect -- graphLineData']);
    dispGraphData(props.checkBoxData.isPileup, props.checkBoxData.isDCRemoval);
  }, [graphLineData]);

  useEffect(() => {
    console.debug([
      'useEffect -- props.checkBoxData, props.selectMSecondData',
      props.checkBoxData,
      props.selectMSecondData,
      graphLineData,
    ]);
    //時間軸最大値が10000越え時、 全点数取得処理
    if (
      graphLineData !== undefined &&
      graphLineData.RGrahData.SamplingCount == 100000 &&
      props.selectMSecondData.maxSec > 10000
    ) {
      getGraphData(props.selectMSecondData.maxSec);
      return;
    }
    //表示更新
    props.updateLoadginState(true);
    dispGraphData(props.checkBoxData.isPileup, props.checkBoxData.isDCRemoval);
  }, [props.checkBoxData, props.selectMSecondData]);

  /**
   * グラフデータを返す
   * @param maxMSec 時間軸最大値が10000以上の場合は全データ取得
   */
  const getGraphData = (maxMSec: number) => {
    props.updateLoadginState(true);
    var selData = props.selectData;

    console.debug(['call getGraphData()', selData]);
    const ms = maxMSec > 10000 ? -1 : 10000;
    GetPhaseCurrentGraph(selData.devIndex, selData.selectDay + ' ' + selData.selectTime, ms)
      .then((ret) => {
        console.debug([`${ms == 10000 ? '10000点' : '全点'} 取得 selData,ret`, selData, ret]);
        if (ret.RGrahData == null || ret.SGrahData == null || ret.TGrahData == null) {
          //console.debug(["★★RGrahData,SGrahData,TGrahData"], ret.RGrahData, ret.SGrahData, ret.TGrahData)
        } else {
          //console.debug(["★★RGrahData,SGrahData,TGrahData"], ret.RGrahData, ret.SGrahData, ret.TGrahData)
          setGraphLineData({ ...ret });
        }
      })
      .catch((error) => {
        console.error('error : ' + error);
        props.updateLoadginState(false);
      });
  };

  /**
   * Y軸範囲検索
   * @param data　データ
   * @param isDCRemoval 直流成分除去フラグ
   * @returns
   */
  const getMaxYValue = (data: GraphLineDatas, isDCRemoval: boolean): number => {
    const rMax = isDCRemoval ? data.RGrahData.MaxValue - data.RGrahData.YaxisOffset : data.RGrahData.MaxValue;
    const sMax = isDCRemoval ? data.SGrahData.MaxValue - data.SGrahData.YaxisOffset : data.SGrahData.MaxValue;
    const tMax = isDCRemoval ? data.TGrahData.MaxValue - data.TGrahData.YaxisOffset : data.TGrahData.MaxValue;
    console.debug(['rMax,sMax,tMax', rMax, sMax, tMax]);

    const rMin = isDCRemoval ? data.RGrahData.MinValue - data.RGrahData.YaxisOffset : data.RGrahData.MinValue;
    const sMin = isDCRemoval ? data.SGrahData.MinValue - data.SGrahData.YaxisOffset : data.SGrahData.MinValue;
    const tMin = isDCRemoval ? data.TGrahData.MinValue - data.TGrahData.YaxisOffset : data.TGrahData.MinValue;

    var aMax = Math.max(rMax, sMax, tMax);
    var aMin = Math.min(rMin, sMin, tMin);

    aMax = ToValueMaxRound(aMax);
    aMin = ToValueMinRound(aMin);
    aMax = Math.abs(aMax);
    aMin = Math.abs(aMin);

    const yMax = Math.max(aMax, aMin);
    //console.debug(['Y軸最大、最小値計算結果', aMax, aMin, yMax]);
    return yMax;
  };

  /**
   *オートスケールの最大値を取得します
   * @param ymax
   * @returns
   */

  const toAutoScaleMax = (ymax: number): number => {
    var resultMax: number;
    var tempMaxValue: number;
    var i: number;
    resultMax = ymax;

    for (i = 0; i < 9; i++) {
      tempMaxValue = resultMax * Math.pow(10, i);
      if (tempMaxValue >= 1) {
        resultMax = Math.round(Math.ceil(tempMaxValue) / Math.pow(10, i));
      }
    }
    //console.debug(['オートスケールの最大値', resultMax]);
    return resultMax;
  };

  const dispGraphData = (isPileup: boolean, isDCRemoval: boolean) => {
    console.debug(['call dispGraphData()', isPileup]);
    var setDt: GraphData[] = [];
    var tmp: GraphLineData[] = [];

    var gSetting = graphSetting;
    if (graphLineData !== undefined) {
      var yMax = getMaxYValue(graphLineData, isDCRemoval);
      var scaledMax = toAutoScaleMax(yMax);
      var offset = scaledMax * 0.5;
      //console.debug(['オフセット', offset]);
      gSetting.yMax = scaledMax;
      gSetting.yMin = scaledMax * -1;

      gSetting.yUnit = graphLineData.RGrahData.Unit;
      console.debug(['dispGraphData - graphLineData', graphLineData]);

      tmp.push(graphLineData.RGrahData);
      tmp.push(graphLineData.SGrahData);
      tmp.push(graphLineData.TGrahData);

      const axisMin: number = props.selectMSecondData.minSec;
      const axisMax: number = props.selectMSecondData.maxSec;
      gSetting.xMin = axisMin;
      gSetting.xMax = axisMax;

      // console.debug(['gSetting', gSetting]);
      for (let i = 0; i < tmp.length; i++) {
        var color = '#ff0000';
        var isDisp = '#ff0000';
        if (i === 1) color = '#0000ff';
        if (i === 2) color = '#008000';

        var item: GraphData = {
          lineNo: Number(i),
          valueUpper: 0,
          valueUpperLimit: 0,
          selectFlg: true,
          points: [],
          infoColor: color,
          lineName: String(i + 1),
        };

        var startIndex = graphLineData.RGrahData.XaxisStartIndex;
        var endIndex = startIndex + graphLineData.RGrahData.XaxisSpanCount * 6;
        console.debug(['開始点,終了点', startIndex, endIndex]);
        if (isPileup) {
          startIndex = tmp[i].XaxisStartIndex;
          endIndex = startIndex + tmp[i].XaxisSpanCount * 6;
        }

        var startSampleIndex = startIndex + Math.ceil(axisMin / tmp[i].SamplingPeriod);
        var endSampleIndex = startIndex + Math.trunc(axisMax / tmp[i].SamplingPeriod);
        startSampleIndex = startSampleIndex >= tmp[i].XValues.length ? tmp[i].XValues.length - 1 : startSampleIndex;
        endSampleIndex = endSampleIndex >= tmp[i].XValues.length ? tmp[i].XValues.length - 1 : endSampleIndex;
        console.debug(['開始点2,終了点2', startSampleIndex, endSampleIndex]);
        for (let j = startSampleIndex; j < endSampleIndex; j++) {
          const xLabel = (j - startSampleIndex) * tmp[i].SamplingPeriod + axisMin;
          item.points.push({
            xValue: xLabel,
            yValue: isDCRemoval ? Number(tmp[i].YValues[j]) - Number(tmp[i].YaxisOffset) : Number(tmp[i].YValues[j]),
          });
        }
        setDt.push(item);

        // console.debug(item.points);
      }

      for (let i = 0; i < setDt.length; i++) {
        if (i === 0) {
          setDt[i].selectFlg = props.checkBoxData.isRDisp;
          setDt[i].lineName = graphLineData.RGrahData.ItemName;
        } else if (i === 1) {
          setDt[i].selectFlg = props.checkBoxData.isSDisp;
          setDt[i].lineName = graphLineData.SGrahData.ItemName;
        } else if (i === 2) {
          setDt[i].selectFlg = props.checkBoxData.isTDisp;
          setDt[i].lineName = graphLineData.TGrahData.ItemName;
        }
      }

      console.debug(['grahp data ', setDt]);
      setGraphData([...setDt]);
      setGraphSetting(gSetting);
    }
    props.updateLoadginState(false);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active) {
      return (
        <div className={classes.customTooltip}>
          <div className="label">{`${t('PhaseCurrentChart.時間', '時間')} : ${Number(label).toFixed(3)}`}</div>
          {payload?.map((r: any, index: any) => {
            return (
              <div style={{ color: r.color }}>
                {r.name} : {Number(r.value).toFixed(3)}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer className={props.chartContainerStyle} width="99%">
      <LineChart
        style={{ background: graphSetting.backColor }}
        width={500}
        height={300}
        margin={{ top: 10, right: 30, left: 1, bottom: 0 }}
      >
        <CartesianGrid stroke={graphSetting.gridColor} strokeDasharray="3 3" />
        <XAxis
          dataKey="xValue"
          type="number"
          allowDuplicatedCategory={false}
          stroke={graphSetting.memoryFontColor}
          tickFormatter={(props) => String(Math.ceil(props))}
          domain={[graphSetting.xMin, graphSetting.xMax]}
          height={50}
          ticks={GetMemoryScaleByNumber(graphSetting.xMin, graphSetting.xMax, 10)}
          // tickCount={6}
          tick={{ dy: 10 }}
          padding={{ right: 0, left: 0 }}
        >
          <Label angle={0} position="insideBottomRight" style={{ textAnchor: 'middle', padding: 0 }}>
            msec
          </Label>
        </XAxis>
        <YAxis
          allowDataOverflow={true}
          ticks={[graphSetting.yMin, (graphSetting.yMin / 2), 0, (graphSetting.yMax / 2), graphSetting.yMax]}
          dataKey="yValue"
          type="number"
          tickFormatter={(props) => props.toFixed(2)}
          stroke={graphSetting.memoryFontColor}
          domain={[graphSetting.yMin, graphSetting.yMax]}
          width={85}
        >
          <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle', padding: 0 }}>
            {graphSetting.yUnit}
          </Label>
        </YAxis>
        <Tooltip content={<CustomTooltip />} />
        {graphData?.map((row, index) => (
          <Line
            connectNulls
            dataKey="yValue"
            stroke={row.infoColor}
            data={row.points}
            strokeWidth={2}
            dot={false}
            name={row.lineName}
            hide={!row.selectFlg}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
export default PhaseCurrentChart;
